import { useMemo, useState } from 'react'
import { useBrand } from 'config/brand'
import { Link, useNavigate } from 'react-router-dom'
import * as api from 'api/entityApi'
import { getApiInfo, getSessionContext } from 'api/entityApi'
import { EmptyState, ErrorState, InlineLoader } from 'components/common/Loader'
import { useAsyncData } from 'hooks/useAsyncData'
import { encryptQueryParams, flowEncrypt } from 'utils/crypto'
import { editLeadToken } from 'utils/entityParams'
import type { EntityType } from 'types/entity'

/** Every branch of the legacy `jsEntityObj.CreateEntity` switch. */
const ENTITY_TYPES: EntityType[] = [
  'lead',
  'enquiry',
  'task',
  'appointment',
  'followup',
  'physicalappointment',
  'calllogdetail',
]

/** Only lead and enquiry have an edit screen — the legacy edit switch matched. */
const EDITABLE: EntityType[] = ['lead', 'enquiry']

/**
 * Stand-in for however the host app links into these screens. The legacy pages
 * were reached as EntityViewDetails.aspx?entityType=lead&entityID=123 or ?q=EncryptedString
 */
export default function Home() {
  useBrand('Entity View / Edit')
  const navigate = useNavigate()
  const [entityType, setEntityType] = useState<EntityType>('lead')
  const [entityId, setEntityId] = useState('100916')
  const api = getApiInfo()

  // The whole query string — keys and values, entity AND session identity —
  // is encrypted into the single `q=` parameter. On open, resolveEntityParams
  // decrypts it and uses UserId/ParentId/ModuleCode/TimeZone from the link.
  const encryptedQuery = useMemo(() => {
    if (!entityId.trim()) return ''
    const session = getSessionContext()
    return encryptQueryParams({
      entityType,
      entityId: entityId.trim(),
      UserId: session.userId,
      ParentId: session.parentId,
      ModuleCode: session.moduleCode,
      TimeZone: session.timeZone,
      EditLead: editLeadToken(),
    })
  }, [entityType, entityId])

  /** The shape every link inside the legacy app uses (jsFlowCommon.Encrypt + EditLead token). */
  const legacyQuery = useMemo(() => {
    if (!entityId.trim()) return ''
    return `entityType=${entityType}&entityID=${flowEncrypt(entityId.trim())}&EditLead=${editLeadToken()}`
  }, [entityType, entityId])

  return (
    <div className="app-shell">
      <header className="page-header">
        <div>
          <h1>Entity View / Edit</h1>
          <div className="crumbs">React port of EntityViewDetails.aspx and EntityEdit.aspx</div>
        </div>
      </header>

      {api.useMock ? (
        <div className="alert alert-success">
          Running on sample data (<code>VITE_USE_MOCK=true</code>). Set it to <code>false</code> in{' '}
          <code>.env.local</code>, then set <code>VITE_API_ORIGIN</code> and <code>VITE_API_TOKEN</code> to
          call the REST API.
        </div>
      ) : (
        <div className={`alert ${api.hasToken ? 'alert-success' : 'alert-error'}`}>
          Live mode — posting to <code>{api.baseUrl}</code>.
          {!api.hasToken && ' No VITE_API_TOKEN is set, so every request will be rejected.'}
        </div>
      )}

      <div className="card">
        <div className="edit-grid">
          <div className="form-group">
            <label>Entity type</label>
            <select
              className="form-control"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value as EntityType)}
            >
              {ENTITY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Entity ID</label>
            <input
              className="form-control"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              placeholder="e.g. 100916"
            />
          </div>
        </div>

        <div className="page-actions" style={{ marginBottom: 12 }}>
          <button
            type="button"
            className="btn pri-btn"
            disabled={!entityId.trim()}
            onClick={() => navigate(`/view/${entityType}/${entityId.trim()}`)}
          >
            Open View (Standard URL)
          </button>

          <button
            type="button"
            className="btn"
            disabled={!entityId.trim() || !EDITABLE.includes(entityType)}
            title={EDITABLE.includes(entityType) ? undefined : `${entityType} has no edit screen`}
            onClick={() => navigate(`/edit/${entityType}/${entityId.trim()}`)}
          >
            Open Edit (Standard URL)
          </button>
        </div>

        <div className="page-actions" style={{ marginBottom: 16 }}>
          <button
            type="button"
            className="btn pri-btn"
            disabled={!entityId.trim()}
            onClick={() => navigate(`/view?${encryptedQuery}`)}
            title="Navigates using encrypted query string parameter ?q=..."
          >
            Open View (Encrypted Query String)
          </button>

          <button
            type="button"
            className="btn"
            disabled={!entityId.trim() || !EDITABLE.includes(entityType)}
            onClick={() => navigate(`/edit?${encryptedQuery}`)}
            title="Navigates using encrypted query string parameter ?q=..."
          >
            Open Edit (Encrypted Query String)
          </button>
        </div>

        <div className="page-actions" style={{ marginBottom: 16 }}>
          <button
            type="button"
            className="btn pri-btn"
            disabled={!entityId.trim()}
            onClick={() => navigate(`/view?${legacyQuery}`)}
            title="The legacy EntityViewDetails.aspx link shape (entityID = jsFlowCommon.Encrypt)"
          >
            Open View (Legacy entityID)
          </button>

          <button
            type="button"
            className="btn"
            disabled={!entityId.trim() || !EDITABLE.includes(entityType)}
            onClick={() => navigate(`/edit?${legacyQuery}`)}
            title="The legacy EntityEdit.aspx link shape (entityID = jsFlowCommon.Encrypt)"
          >
            Open Edit (Legacy entityID)
          </button>
        </div>

        {encryptedQuery && (
          <div style={{ background: 'var(--bg)', padding: 12, borderRadius: 8, fontSize: 13, wordBreak: 'break-all', marginBottom: 16 }}>
            <strong>Generated Encrypted Query String:</strong> <code>?{encryptedQuery}</code>
            <div style={{ marginTop: 8 }}>
              <strong>Legacy link:</strong> <code>?{legacyQuery}</code>
            </div>
          </div>
        )}

        <div style={{ background: 'var(--info-soft)', border: '1px solid var(--info-soft)', padding: 12, borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
          <strong>User Sample Link Test:</strong>
          <div style={{ wordBreak: 'break-all', margin: '4px 0 8px', color: 'var(--info)', fontFamily: 'monospace' }}>
            UserCRMCampaign/EntityEdit.aspx?entityType=lead&entityID=U2FsdGVkX18yQ8CqRlgN8HRrBu8kAc4IbFNEleY3myc%3D&EditLead=20268112
          </div>
          <div className="page-actions">
            <button
              type="button"
              className="btn pri-btn"
              onClick={() => navigate('/UserCRMCampaign/EntityEdit.aspx?entityType=lead&entityID=U2FsdGVkX18yQ8CqRlgN8HRrBu8kAc4IbFNEleY3myc%3D&EditLead=20268112')}
            >
              Open Sample (ASPX Path)
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => navigate('/edit?entityType=lead&entityID=U2FsdGVkX18yQ8CqRlgN8HRrBu8kAc4IbFNEleY3myc%3D&EditLead=20268112')}
            >
              Open Sample (/edit Path)
            </button>
          </div>
        </div>

        <hr />

        <RecentLeads />
      </div>
    </div>
  )
}

/**
 * Real records pulled from the API, so the launcher links to leads that
 * actually exist in this account rather than fixed sample ids.
 */
function RecentLeads() {
  const { data, loading, error, reload } = useAsyncData(
    (signal) => api.fetchLeadList(25, signal),
    [],
  )

  if (loading) return <InlineLoader label="Loading recent leads…" />
  if (error) return <ErrorState message={`Could not load leads: ${error}`} onRetry={reload} />
  if (!data || data.length === 0) return <EmptyState message="No leads returned for this user." />

  return (
    <>
      <h5 style={{ fontSize: 15, margin: '0 0 8px' }}>Recent leads</h5>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Lead No</th>
              <th>Name</th>
              <th>Company</th>
              <th>Mobile</th>
              <th>Open</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.entityId}>
                <td>{row.entityNo}</td>
                <td>{row.personName || '—'}</td>
                <td>{row.companyName || '—'}</td>
                <td>{row.mobileNo || '—'}</td>
                <td>
                  <Link to={`/view/lead/${row.entityId}`}>View</Link>
                  {' · '}
                  <Link to={`/edit/lead/${row.entityId}`}>Edit</Link>
                  {' · '}
                  <Link to={`/view?${encryptQueryParams({ entityType: 'lead', entityId: row.entityId })}`}>
                    View (encrypted)
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
